/**
 * CarrierAdapter nach Recherche 02, Abschnitt 5.3 (uebernommen, Adresse auf
 * das StoreOS-Adressmodell umgestellt). Implementierungen: dhl (Parcel DE v2),
 * shipcloud, mock. Auswahl per Konfiguration je Marke/Brand, nicht je Sendung.
 */
import type { Adresse } from './adresse.js';
import type { CarrierStatus } from './status.js';
import type { LabelFormat } from './entitaeten.js';
import type { IntegrationMode } from './kanal.js';
import { istEuLand } from './laender.js';

export type ParcelProduct = 'parcel' | 'small_parcel' | 'parcel_eu' | 'parcel_international';

/** B9/R61: Klartext je Versandprodukt. Rohwerte wie `small_parcel` erscheinen nie in der Oberflaeche oder auf Belegen. */
export const VERSANDPRODUKT_TEXT: Record<ParcelProduct, string> = {
  small_parcel: 'DHL Kleinpaket',
  parcel: 'DHL Paket',
  parcel_eu: 'DHL Paket International',
  parcel_international: 'DHL Paket International',
};

export function versandproduktText(p: string | null | undefined): string {
  if (!p) return '–';
  return VERSANDPRODUKT_TEXT[p as ParcelProduct] ?? 'Versandprodukt unbekannt';
}

/** DHL Kleinpaket: bis 1 kg (Recherche 04 §5; vor Vertragsabschluss mit DHL bestaetigen). */
export const KLEINPAKET_MAX_G = 1000;

/**
 * Versandprodukt aus Land, eingetragenem Gewicht und Verpackung (R41). EIN Ort fuer API und Packstation, damit die
 * Anzeige beim Tippen dieselbe Regel nutzt wie das Label: DE + ≤ 1000 g + Versandtasche (Nr. 1/2) → Kleinpaket.
 */
export function versandproduktFuer(land: string, gewichtG: number, verpackungNr: number | null, kleinpaketErlaubt = true): ParcelProduct {
  if (land !== 'DE') return istEuLand(land) ? 'parcel_eu' : 'parcel_international';
  // Kleinpaket nur, wenn freigeschaltet (DHL-Abrechnungsnummer vorhanden, Mitnahme bei der Abholung zugesagt; Befund S1).
  if (kleinpaketErlaubt && gewichtG <= KLEINPAKET_MAX_G && (verpackungNr === 1 || verpackungNr === 2)) return 'small_parcel';
  return 'parcel';
}

export interface CreateShipmentInput {
  /** Idempotenzschluessel auf StoreOS-Seite (≥ 8 Zeichen, erscheint als DHL refNo). */
  orderRef: string;
  from: Adresse;
  to: Adresse;
  weightGrams: number;
  dimensionsCm?: { l: number; w: number; h: number };
  product: ParcelProduct;
  preferredDay?: string;
  preferredNeighbour?: string;
  preferredLocation?: string;
  notifyEmail?: string;
  labelFormat: LabelFormat;
  includeReturnLabel?: boolean;
}

export interface LabelDoc {
  contentType: 'application/pdf' | 'application/zpl';
  /** Adapter laden URL-Labels sofort und liefern Bytes; URLs verfallen (DHL). */
  data: Uint8Array;
  url?: string;
}

export interface CreateShipmentResult {
  carrier: 'shipcloud' | 'dhl' | 'mock';
  isMock: boolean;
  carrierShipmentId: string;
  trackingNumber: string;
  trackingUrl?: string;
  label: LabelDoc;
  returnLabel?: LabelDoc;
  priceCents?: number;
  warnings: string[];
}

export interface TrackingEventInfo {
  at: string;
  status: CarrierStatus;
  location?: string;
  rawStatus: string;
  description?: string;
}

export interface TrackingInfo {
  trackingNumber: string;
  status: CarrierStatus;
  events: TrackingEventInfo[];
  estimatedDelivery?: string;
}

export interface CarrierWebhookEvent {
  carrierShipmentId: string;
  trackingNumber?: string;
  status: CarrierStatus;
  rawType: string;
  occurredAt: string;
  /** Shipcloud: Payload ohne Details → getTracking nachladen. */
  needsFetch: boolean;
}

export type CarrierErrorCode =
  | 'address_invalid' | 'packstation_invalid' | 'auth' | 'rate_limited' | 'plan_limit'
  | 'carrier_unavailable' | 'not_cancellable' | 'validation' | 'unknown'
  /** R125: Carrier kann keine API-Abholung bzw. Vorlauf unterschritten (fachlich, kein R34-Fallback). */
  | 'pickup_not_supported' | 'pickup_too_late';

export class CarrierError extends Error {
  constructor(
    public code: CarrierErrorCode,
    message: string,
    public fieldErrors?: Array<{ field: string; message: string }>,
  ) {
    super(message);
    this.name = 'CarrierError';
  }
}

/**
 * R34: Fallback auf den zweiten Carrier NUR bei technischem Fehler (5xx, Timeout, Netz, Rate-Limit),
 * NIE bei Adress- oder Validierungsfehlern (die waeren beim zweiten Carrier genauso falsch).
 */
export function istTechnischerCarrierFehler(err: unknown): boolean {
  if (err instanceof CarrierError) return err.code === 'carrier_unavailable' || err.code === 'rate_limited' || err.code === 'unknown';
  // Kein CarrierError: Netzfehler, Abbruch durch Timeout (AbortError/TimeoutError), fetch failed.
  return err instanceof Error;
}

export interface CarrierAdapter {
  readonly id: 'shipcloud' | 'dhl' | 'mock';
  readonly mode: IntegrationMode;
  readonly supportsWebhooks: boolean;

  createShipment(input: CreateShipmentInput, opts?: { validateOnly?: boolean }): Promise<CreateShipmentResult>;
  cancelShipment(carrierShipmentId: string): Promise<void>;
  createReturn(input: { orderRef: string; customer: Adresse; weightGrams?: number; labelType?: 'label' | 'qr' | 'both' }):
    Promise<CreateShipmentResult & { qrCodePng?: Uint8Array; qrLink?: string }>;
  /** Shipcloud und Mock akzeptieren statt der Trackingnummer auch die `carrierShipmentId` (der Webhook kennt nur diese). */
  getTracking(trackingNumber: string, ctx?: { recipientPostalCode?: string }): Promise<TrackingInfo>;
  parseWebhook(headers: Record<string, string | string[] | undefined>, rawBody: string): Promise<CarrierWebhookEvent | null>;
  /** Tagesabschluss beim Carrier. DHL braucht fuer POST /manifests ggf. Sendungsnummern (OFFEN-DHL-3); dafuer `carrierShipmentIds`. */
  closeDay?(opts?: { carrierShipmentIds?: string[] }): Promise<{ manifestPdf?: Uint8Array }>;

  // R120/R125 (12-versandrouting.md § 5.2): optionale Methoden, Stufe 1 nur als Vertrag. DHL und Shipcloud implementieren
  // sie noch nicht; fehlt eine Methode, gilt die Tariftabelle bzw. der Abholauftrag wird manuell gepflegt.
  /** Live-Preise des Anbieters (Stufe 2+). Ersetzt nie die Tariftabelle (R113). */
  quote?(input: QuoteInput): Promise<Quote[]>;
  /** Abholung beauftragen (Idempotenzschlüssel `pickup:<konto>:<tag>`). */
  pickup?(input: PickupInput): Promise<PickupResult>;
  /** Beauftragte Abholung stornieren. */
  cancelPickup?(carrierPickupId: string): Promise<void>;
  /** Welche Carrier des Kontos Abholung per API können. */
  pickupCapabilities?(): Promise<PickupCapability[]>;
}

/** Eingabe für eine Preisabfrage (§ 5.2). */
export interface QuoteInput {
  from: Adresse;
  to: Adresse;
  weightGrams: number;
  dimensionsCm: { l: number; w: number; h: number };
  goodsValueCents: number;
  dropPointType: 'adresse' | 'packstation' | 'postfiliale';
  /** 'YYYY-MM-DD' (Peak-Zuschläge). */
  shipDate: string;
  /** Einschränkung auf Produktcodes des Anbieters. */
  products?: string[];
}

export interface Quote {
  /** Kontokennung beim Anbieter → versand_konten.code. */
  accountRef: string;
  product: string;
  carrier: string;
  priceCents: number;
  includesSurcharges: boolean;
  currency: 'EUR';
  transitDays?: number;
  validUntil?: string;
  /** Nie loggen. */
  raw?: unknown;
}

export interface PickupInput {
  accountRef: string;
  /** 'YYYY-MM-DD' */
  date: string;
  /** ISO 8601 mit Zeitzone. */
  earliest: string;
  latest: string;
  /** Abholadresse = brands.versand_absender. */
  address: Adresse;
  /** Leer = alle offenen (Shipcloud-Semantik). */
  parcels?: Array<{ carrierShipmentId: string; weightGrams: number }>;
  count: number;
  totalWeightGrams?: number;
  idempotencyKey: string;
}

export interface PickupResult {
  carrierPickupId: string;
  status: 'requested' | 'confirmed';
  confirmedWindow?: { earliest: string; latest: string };
  /** Nie loggen. */
  raw?: unknown;
}

export interface PickupCapability { carrier: string; leadDays: number; windowRequired: boolean }

/** Eingabe fuer das Mock-Label (Belege-Paket rendert, Carrier-Mock ruft). */
export interface MockLabelInput {
  format: LabelFormat;
  from: Adresse;
  to: Adresse;
  trackingNumber: string;
  orderRef: string;
  product: ParcelProduct;
  weightGrams: number;
  isReturn: boolean;
}
